import React from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import { theme } from '../styles/GlobalStyles';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const Card = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(97,93,255,0.15);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  background: ${theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CardContent = styled.p`
  color: ${theme.textMuted};
  line-height: 1.7;
  margin: 0;
`;

const StoryContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-top: 3rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const StoryImage = styled.div`
  background-image: url('https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80');
  background-size: cover;
  background-position: center;
  border-radius: 16px;
  height: 400px;
  border: 1px solid ${theme.border};

  @media (max-width: 992px) { height: 280px; }
`;

const StoryContent = styled.div`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.8;
    font-size: 1rem;
    color: ${theme.textMuted};
  }
`;

const TeamSection = styled.div`
  margin-top: 5rem;
`;

const AboutPage = () => (
  <>
    <PageBanner
      title="About Us"
      subtitle="Get to know the team behind Prodseal's innovative product authentication technology."
    />

    <ContentSection>
      <Grid>
        <Card>
          <CardTitle>Our Vision</CardTitle>
          <CardContent>
            A safer, more transparent marketplace where every consumer can trust the product they purchase.
          </CardContent>
        </Card>
        <Card>
          <CardTitle>Our Mission</CardTitle>
          <CardContent>
            To protect brands and empower consumers through simple, powerful, and secure product authentication technology.
          </CardContent>
        </Card>
      </Grid>

      <StoryContainer>
        <StoryContent>
          <SectionTitle>Our Story</SectionTitle>
          <p>
            Founded with the belief that trust is the foundation of every purchase,
            Prodseal was built to give businesses the tools to fight counterfeiting
            while offering added value like product authenticity check, warranty activation,
            supply chain insights, and customer engagement.
          </p>
          <p>
            Our journey began when our founder, always conscious and worried
            about fake products floating in the market, realized their brand was
            unable to help customers gain trust. This drove the decision to
            implement an intelligent solution so that brands can grow and
            customers get empowered.
          </p>
          <p>
            Today, Prodseal's technology is ready to protect millions of products across
            various industries, ensuring consumers get exactly what they paid for
            and brands maintain their reputation and revenue.
          </p>
        </StoryContent>
        <StoryImage />
      </StoryContainer>

      <TeamSection>
        <SectionTitle>Meet Our Team</SectionTitle>
        <Grid>
          <Card>
            <CardTitle>Leadership Team</CardTitle>
            <CardContent>
              Our leadership brings decades of combined experience in cybersecurity,
              supply chain management, and consumer product industries, dedicated to
              creating solutions that work for real-world challenges.
            </CardContent>
          </Card>
          <Card>
            <CardTitle>Engineering Excellence</CardTitle>
            <CardContent>
              Prodseal's engineering team consists of experts in encryption,
              verification systems, and user experience design — building technology
              that's both highly secure and remarkably easy to use.
            </CardContent>
          </Card>
          <Card>
            <CardTitle>Customer Success</CardTitle>
            <CardContent>
              Beyond our technology, our customer success team ensures every
              client achieves their authentication goals — from implementation
              support to ongoing optimisation at every step.
            </CardContent>
          </Card>
        </Grid>
      </TeamSection>
    </ContentSection>
  </>
);

export default AboutPage;
